import type { ExperienceContext, UserContextData } from '../types'
import { useExperienceStore } from './experienceStore'
import { useThemeStore } from '@/shared/store/themeStore'

/**
 * Resolves the current time of day based on hours (0-23)
 */
function getTimeOfDay(hours: number): 'morning' | 'afternoon' | 'evening' | 'night' {
  if (hours >= 5 && hours < 12) return 'morning'
  if (hours >= 12 && hours < 17) return 'afternoon'
  if (hours >= 17 && hours < 21) return 'evening'
  return 'night'
}

/**
 * Resolves the season based on month (0-11)
 */
function getSeason(month: number): 'spring' | 'summer' | 'autumn' | 'winter' {
  if (month >= 2 && month <= 4) return 'spring' // Mar - May
  if (month >= 5 && month <= 7) return 'summer' // Jun - Aug
  if (month >= 8 && month <= 10) return 'autumn' // Sep - Nov
  return 'winter' // Dec - Feb
}

/**
 * Checks if the current date matches major holidays
 */
function getHoliday(date: Date): string | null {
  const month = date.getMonth() + 1 // 1-12
  const day = date.getDate()

  const mmdd = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  switch (mmdd) {
    case '01-01':
      return 'New Year'
    case '02-14':
      return 'Valentines Day'
    case '07-04':
      return 'Independence Day'
    case '10-31':
      return 'Halloween'
    case '11-26':
    case '11-27':
    case '11-28':
      return 'Thanksgiving' // Approximately
    case '12-24':
      return 'Christmas Eve'
    case '12-25':
      return 'Christmas'
    case '12-31':
      return 'New Years Eve'
    default:
      return null
  }
}

/**
 * Resolves client OS platform
 */
function getPlatform(): 'mac' | 'windows' | 'linux' | 'mobile' | 'unknown' {
  if (typeof window === 'undefined') return 'unknown'
  const userAgent = window.navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod|android|webos|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return 'mobile'
  }
  const platform = window.navigator.platform.toLowerCase()
  if (platform.includes('mac')) return 'mac'
  if (platform.includes('win')) return 'windows'
  if (platform.includes('linux')) return 'linux'
  return 'unknown'
}

/**
 * Generates the full ExperienceContext object based on active app state
 */
export function getExperienceContext(
  userProfile: { name: string; email: string; role?: string } | null,
  dashboardSummary?: { currentStreak: number; completedGoals: number } | null,
  featureFlags: Record<string, boolean> = {}
): ExperienceContext {
  const date = new Date()
  const store = useExperienceStore.getState()
  const themeState = useThemeStore.getState()

  const prefersReducedMotion =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false

  const screenSize = {
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  }

  const user: UserContextData = {
    name: userProfile?.name || 'Guest User',
    email: userProfile?.email || 'guest@notimetodelay.com',
    membership: userProfile?.role === 'ADMIN' ? 'PREMIUM' : 'FREE', // Rule-of-thumb mapping for test
    currentStreak: dashboardSummary?.currentStreak || 0,
    completedGoalsCount: dashboardSummary?.completedGoals || 0,
  }

  return {
    timeOfDay: getTimeOfDay(date.getHours()),
    season: getSeason(date.getMonth()),
    holiday: getHoliday(date),
    user,
    featureFlags,
    history: store.history,
    prefersReducedMotion,
    theme: themeState.resolvedTheme || 'dark',
    screenSize,
    platform: getPlatform(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    visitCount: store.visitCount,
    isFirstVisit: store.visitCount <= 1,
  }
}
